import { toSafeCode } from "../app/safeError";

export { toSafeCode } from "../app/safeError";

const SUSPICIOUS_PRIVATE_KEYS = [
  "note",
  "transcript",
  "description",
  "address",
  "filename",
  "image",
  "photo",
  "audio",
  "photodata",
  "audiodata",
  "base64",
  "exif",
  "gps",
  "latitude",
  "longitude",
  "pdf",
  "screenshot",
  "domsnapshot",
  "rawprovidererror"
] as const;

export function assertSafeAnalyticsPayload(payload: unknown): void {
  assertNoSuspiciousKeys(payload);
}

export function sanitizeErrorForAnalytics(
  error: unknown,
  fallbackCode: string
): {
  safeErrorCode: string;
  category?: string;
  errorName?: string;
  module?: string;
  line?: number;
  column?: number;
} {
  if (error instanceof Error) {
    const firstFrame = parseFirstStackFrame(error.stack);
    return {
      safeErrorCode: toSafeCode(fallbackCode || error.name || "UNKNOWN_ERROR"),
      category: error.name || "Error",
      errorName: error.name || "Error",
      ...firstFrame
    };
  }
  if (typeof error === "string") {
    return {
      safeErrorCode: toSafeCode(fallbackCode || "STRING_ERROR"),
      category: "Error"
    };
  }
  return {
    safeErrorCode: toSafeCode(fallbackCode || "UNKNOWN_ERROR"),
    category: "Unknown"
  };
}

export function bucketCharacterCount(length: number): "0" | "1-40" | "41-120" | "121-200" | "201-260" {
  if (length <= 0) {
    return "0";
  }
  if (length <= 40) {
    return "1-40";
  }
  if (length <= 120) {
    return "41-120";
  }
  if (length <= 200) {
    return "121-200";
  }
  return "201-260";
}

export function mimeCategory(mimeType: string | undefined): string {
  const normalized = (mimeType ?? "").toLowerCase();
  if (normalized.includes("jpeg") || normalized.includes("jpg")) {
    return "jpeg";
  }
  if (normalized.includes("png")) {
    return "png";
  }
  if (normalized.includes("webp")) {
    return "webp";
  }
  if (normalized.includes("webm")) {
    return "webm";
  }
  if (normalized.includes("mp4") || normalized.includes("m4a")) {
    return "mp4";
  }
  if (normalized.includes("wav")) {
    return "wav";
  }
  if (normalized.includes("ogg")) {
    return "ogg";
  }
  return normalized.startsWith("audio/") ? "audio_other" : normalized.startsWith("image/") ? "image_other" : "other";
}

function assertNoSuspiciousKeys(value: unknown, path: string[] = []): void {
  if (!value || typeof value !== "object") {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => assertNoSuspiciousKeys(item, [...path, String(index)]));
    return;
  }
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (isSuspiciousPrivateKey(key)) {
      throw new Error(`Analytics payload contains a private field key: ${[...path, key].join(".")}`);
    }
    assertNoSuspiciousKeys(child, [...path, key]);
  }
}

function isSuspiciousPrivateKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (["text", "freetext", "rawtext", "typedtext", "usertext", "message"].includes(normalized)) {
    return true;
  }
  return SUSPICIOUS_PRIVATE_KEYS.some((privateKey) => normalized.includes(privateKey));
}

function parseFirstStackFrame(stack: string | undefined): { module?: string; line?: number; column?: number } {
  if (!stack) {
    return {};
  }
  const frame = stack
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.includes(".js") || line.includes(".ts") || line.includes(".tsx"));
  if (!frame) {
    return {};
  }
  const match = frame.match(/([^/\\()]+?\.(?:js|ts|tsx))(?::(\d+))?(?::(\d+))?/);
  return {
    module: match?.[1]?.slice(0, 100),
    line: match?.[2] ? Number(match[2]) : undefined,
    column: match?.[3] ? Number(match[3]) : undefined
  };
}
